import { Injectable } from '@angular/core';
import { CurrentUserService } from './current-user.service';
import { filter, map } from 'rxjs/operators';
import { UpdateUserLanguageGQL } from '../graphql/gql';
import { lastValueFrom, BehaviorSubject } from 'rxjs';

export type SupportedLanguage = 'en' | 'de' | 'fr';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly supportedLanguages: SupportedLanguage[] = ['en', 'de', 'fr'];
  public readonly defaultLanguage: SupportedLanguage = 'en';
  private readonly languageSubject = new BehaviorSubject<SupportedLanguage>(this.defaultLanguage);
  public readonly currentLanguage$ = this.languageSubject.asObservable();

  constructor(
    private currentUserService: CurrentUserService,
    private updateUserLanguageGQL: UpdateUserLanguageGQL,
  ) {}

  /**
   * Initializes language switching based on user preference
   * Redirects to user's preferred language if different from current URL
   */
  initializeLanguageSwitching(): void {
    this.currentUserService.currentUser.pipe(
      filter(userData => !!userData?.currentUser),
      map(userData => {
        const user = userData!.currentUser!;
        return (user as any).settings?.language;
      })
    ).subscribe(userLanguage => {
      if (userLanguage && this.isLanguageSupported(userLanguage)) {
        const currentUrlLanguage = this.getCurrentLanguage();

        // If user's preferred language differs from current URL, redirect
        if (userLanguage !== currentUrlLanguage) {
          console.log(`User prefers ${userLanguage}, but current URL shows ${currentUrlLanguage}. Redirecting...`);
          this.redirectToLanguageVersion(userLanguage);
        } else {
          console.log(`User language preference matches current URL: ${userLanguage}`);
          this.languageSubject.next(currentUrlLanguage);
        }
      }
    });
  }

  /**
   * Gets the current language from URL path or defaults to 'en'
   */
  getCurrentLanguage(): SupportedLanguage {
    const currentPath = window.location.pathname;

    if (currentPath.startsWith('/de/') || currentPath === '/de') {
      return 'de';
    }
    if (currentPath.startsWith('/fr/') || currentPath === '/fr') {
      return 'fr';
    }

    return this.defaultLanguage;
  }

  /**
   * Switch to a specific language by redirecting with the language prefix
   */
  switchLanguage(language: SupportedLanguage): void {
    if (!this.isLanguageSupported(language)) {
      console.error(`Unsupported language: ${language}`);
      return;
    }

    // Update user's language preference in the backend
    this.updateUserLanguagePreference(language)
      .then(() => {
        console.log(`User language preference updated to: ${language}`);
        // Always redirect to show the language change immediately
        this.redirectToLanguageVersion(language);
      })
      .catch(error => {
        console.error('Failed to update user language preference:', error);
        // Still redirect even if backend update fails
        // Also, should we?
        this.redirectToLanguageVersion(language);
      });
  }

  /**
   * Gets all supported languages for UI components
   */
  getSupportedLanguages(): { code: SupportedLanguage; name: string }[] {
    return [
      { code: 'en', name: 'English' },
      { code: 'de', name: 'Deutsch' },
      { code: 'fr', name: 'Français' }
    ];
  }

  /**
   * Checks if a language is supported
   */
  private isLanguageSupported(language: string): boolean {
    return this.supportedLanguages.includes(language as SupportedLanguage);
  }

  /**
   * Updates the user's language preference via GraphQL mutation
   */
  private async updateUserLanguagePreference(language: SupportedLanguage): Promise<void> {
    try {
      const result = await lastValueFrom(this.updateUserLanguageGQL.mutate({
        language
      }));

      const mutationData = result?.data;
      if (mutationData?.updateUserSettings?.errors && mutationData.updateUserSettings.errors.length > 0) {
        console.error('GraphQL errors:', mutationData.updateUserSettings.errors);
        throw new Error('Failed to update language preference');
      }

      console.log('User language preference updated successfully');
    } catch (error) {
      console.error('Error updating user language preference:', error);
      throw error;
    }
  }

  /**
   * Redirects to the appropriate language version using path-based routing
   */
  private redirectToLanguageVersion(language: SupportedLanguage): void {
    const baseUrl = window.location.origin;
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;

    // Remove existing language prefix from path
    let cleanPath = currentPath;
    if (currentPath.startsWith('/de/')) {
      cleanPath = currentPath.substring(3);
    } else if (currentPath.startsWith('/fr/')) {
      cleanPath = currentPath.substring(3);
    }

    // Ensure clean path starts with /
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }

    // Build new URL with language prefix
    let newPath: string;
    if (language === 'en') {
      newPath = cleanPath;
    } else {
      newPath = `/${language}${cleanPath}`;
    }

    // Construct full URL
    const newUrl = `${baseUrl}${newPath}${currentSearch}${currentHash}`;

    window.location.href = newUrl;
  }
}